import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  FileText,
  ClipboardList,
  Receipt,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Package,
  Maximize,
  Minimize,
  Bell,
  AlertTriangle,
  UserCog,
  ShieldCheck,
  BarChart3,
  Building2,
  Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import DocumentEditorPage from "@/pages/DocumentEditorPage";
import ArticlesPage from "@/pages/ArticlesPage";
import OverviewPage from "@/pages/OverviewPage";
import InactiveClientsPage from "@/pages/InactiveClientsPage";
import UsersPage from "@/pages/UsersPage";
import ReportsPage from "@/pages/ReportsPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyUsersPage from "@/pages/CompanyUsersPage";

const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.log("Error entering fullscreen:", err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.log("Error exiting fullscreen:", err);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);



  const handleLogout = () => {
    logout();
    toast.success("Sesión cerrada");
    navigate("/login");
  };

  const isAdmin = stats?.user_role === "admin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin";

  const navItems = [
    // SuperAdmin items
    ...(isSuperAdmin ? [
      { path: "/superadmin/companies", icon: Building2, label: "Empresas", superadminOnly: true },
    ] : []),
    // Regular items
    { path: "/", icon: LayoutDashboard, label: "Resumen" },
    { path: "/clients", icon: Users, label: "Clientes" },
    { path: "/articles", icon: Package, label: "Artículos" },
    { path: "/inactive-clients", icon: Bell, label: "Seguimiento", alert: true },
    // Admin items
    ...(isAdmin || isSuperAdmin ? [
      { path: "/users", icon: UserCog, label: "Usuarios", adminOnly: true },
      { path: "/reports", icon: BarChart3, label: "Reportes", adminOnly: true },
    ] : []),
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-logo">
          <div className="flex items-center gap-3">
            {
              user.company_logo ? (
                <img
                  src={user.company_logo}
                  className=" w-auto"
                />
              ) : (
                <img
                  src="/logoapp.png"
                  className="h-20 w-auto"
                />
              )
            }


          </div>
          <div className="flex items-center gap-3" style={{ paddingTop: 10 }}>
            <div>
              <h2 className="font-bold text-white text-sm">MiPedidoApp</h2>
              <p className="text-xs text-slate-400">Ventas</p>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="px-4 py-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Menú Principal</p>
          </div>
          {navItems.map((item) => (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              className={`sidebar-nav-item ${isActive(item.path) ? "active" : ""}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
              {item.alert && (
                <span className="ml-auto w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              )}
            </button>
          ))}

          <div className="px-4 py-2 mt-6">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Documentos</p>
          </div>
          <button
            data-testid="nav-orders"
            onClick={() => navigate("/clients")}
            className="sidebar-nav-item"
          >
            <ClipboardList size={20} />
            <span>Pedidos</span>
          </button>
          <button
            data-testid="nav-quotes"
            onClick={() => navigate("/clients")}
            className="sidebar-nav-item"
          >
            <FileText size={20} />
            <span>Cotizaciones</span>
          </button>
          <button
            data-testid="nav-invoices"
            onClick={() => navigate("/clients")}
            className="sidebar-nav-item"
          >
            <Receipt size={20} />
            <span>Facturas</span>
          </button>
        </nav>

        <div className="sidebar-user">
          <Avatar className="h-9 w-9">
            <AvatarFallback className={`text-white text-sm ${isSuperAdmin ? "bg-purple-600" : isAdmin ? "bg-amber-600" : "bg-blue-600"
              }`}>
              {user ? getInitials(user.name) : "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <div className="flex items-center gap-1">
              {isSuperAdmin && <Crown size={12} className="text-purple-400" />}
              {isAdmin && !isSuperAdmin && <ShieldCheck size={12} className="text-amber-400" />}
              <p className="text-xs text-slate-400 truncate">
                {isSuperAdmin ? "SuperAdmin" : isAdmin ? "Administrador" : "Vendedor"}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            data-testid="logout-btn"
            onClick={handleLogout}
            className="text-slate-400 hover:text-white hover:bg-white/10"
          >
            <LogOut size={18} />
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Topbar */}
        <header className="topbar">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">
                {location.pathname === "/" && "Resumen"}
                {location.pathname.startsWith("/clients") && "Clientes"}
                {location.pathname.startsWith("/articles") && "Artículos"}
                {location.pathname.startsWith("/document") && "Documento"}
                {location.pathname.startsWith("/inactive-clients") && "Seguimiento"}
                {location.pathname.startsWith("/users") && "Usuarios"}
                {location.pathname.startsWith("/reports") && "Reportes"}
                {location.pathname === "/superadmin/companies" && "Empresas"}
                {location.pathname.includes("/superadmin/companies/") && location.pathname.includes("/users") && "Usuarios de Empresa"}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {stats && (
              <div className="hidden md:flex items-center gap-6 text-sm">
                <div>
                  <span className="text-slate-500">Balance Pendiente:</span>
                  <span className="ml-2 font-semibold text-slate-800 mono">
                    RD$ {stats.pending_balance?.toLocaleString('es-DO', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
            {!window.ReactNativeWebView && <Button
              variant="outline"
              size="icon"
              data-testid="fullscreen-btn"
              onClick={toggleFullscreen}
              className="h-9 w-9"
              title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
            >
              {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
            </Button>
            }
          </div>
        </header>

        {/* Content Area */}
        <div className="content-area">
          <Routes>
            <Route path="/" element={<OverviewPage stats={stats} />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:clientId" element={<ClientDetailPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/document/new" element={<DocumentEditorPage />} />
            <Route path="/document/:docId" element={<DocumentEditorPage />} />
            <Route path="/inactive-clients" element={<InactiveClientsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/superadmin/companies" element={<CompaniesPage />} />
            <Route path="/superadmin/companies/:companyId/users" element={<CompanyUsersPage />} />
          </Routes>
        </div>
      </main>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
